import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { dashboardWidgetService, divisionService } from '@services';
import { MY_DASHBOARD_KEY, useMyDashboard } from '@hooks/useMyDashboard';
import { useMasonryGrid } from '@hooks/useMasonryGrid';
import { showToast } from '@utils/toast';
import ConfirmDialog from '@components/common/ConfirmDialog';
import ErrorState from '@components/common/ErrorState';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import { LoadingState } from '@components/common/loading';
import { DashboardWidgetCard } from './DashboardWidgetCard';
import { DivisionFilterDropdown } from './DivisionFilterDropdown';
import { SUBSCRIPTIONS_KEY } from './subscriptionsKey';
import type { WidgetSubscription, WidgetSubscriptionSchedule } from '@app-types';
import styles from './MyCustomDashboard.module.css';

const ADD_WIDGET_PLACEHOLDER = 'Add a widget...';

interface PendingRemoval {
  widgetKey: string;
  title: string;
}

/**
 * The Dashboard, shown as the "My Custom Dashboard" tab of the Reports page: where a user places the Dashboard
 * Widgets their roles have access to, orders them, narrows them to Divisions and chooses how often each is emailed
 * to them. It is separate from the Launch Page. The page shell supplies the layout and the tabs.
 */
const MyCustomDashboard: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  // The chosen Divisions, shown at once while the filter is being saved; the saved ones replace it.
  const [pendingDivisionIds, setPendingDivisionIds] = useState<number[] | null>(null);

  const dashboardQuery = useMyDashboard(!!user);
  const subscriptionsQuery = useQuery({
    queryKey: SUBSCRIPTIONS_KEY,
    queryFn: () => dashboardWidgetService.getSubscriptions(),
    enabled: !!user
  });
  const divisionsQuery = useQuery({
    queryKey: ['active-divisions'],
    queryFn: () => divisionService.getActive(),
    enabled: !!user
  });

  // Every change goes through here, so the controls wait for the save and the page reloads what it changed.
  const change = useMutation({
    mutationFn: (action: () => Promise<void>) => action(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: MY_DASHBOARD_KEY }),
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY })
      ]),
    onError: () => showToast.error('The change could not be saved. Please try again.')
  });
  const busy = change.isPending;

  // Each widget starts right under the one above it in its column, not under the tallest widget of the row above.
  const widgetsRef = useRef<HTMLDivElement>(null);
  useMasonryGrid(widgetsRef, (dashboardQuery.data?.placements ?? []).map((placement) => placement.widget_key).join(','));

  if (dashboardQuery.isLoading) {
    return <LoadingState message="Loading your Dashboard..." spinnerSize="large" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        data-test-id="my-dashboard-error"
        icon={<AlertCircle size={24} />}
        message="Failed to load your Dashboard."
        action={
          <Button variant="secondary" size="sm" onClick={() => dashboardQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const { available_widgets: available, placements, division_ids: divisionIds } = dashboardQuery.data;

  // A user whose roles have no Dashboard Widget has no Dashboard; the Reports page does not offer this tab to them.
  // Only an Admin removing the last Widget Access while the tab is open gets here.
  if (available.length === 0) {
    return (
      <p className={styles.empty} data-test-id="my-dashboard-no-widgets">
        No Dashboard Widgets are available to your roles.
      </p>
    );
  }

  const titleOf = (widgetKey: string) => available.find((widget) => widget.key === widgetKey)?.title ?? widgetKey;
  const placedKeys = placements.map((placement) => placement.widget_key);
  const addable = available.filter((widget) => !placedKeys.includes(widget.key));
  const subscriptionOf = (widgetKey: string): WidgetSubscription | undefined =>
    subscriptionsQuery.data?.subscriptions.find((subscription) => subscription.widget_key === widgetKey);

  const changeDivisions = (ids: number[]) => {
    setPendingDivisionIds(ids);
    change.mutate(() => dashboardWidgetService.setDivisionFilter(ids), {
      // Success reloads the saved filter; failure puts the previous one back.
      onSettled: () => setPendingDivisionIds(null)
    });
  };

  const move = (index: number, offset: -1 | 1) => {
    const order = [...placedKeys];
    [order[index], order[index + offset]] = [order[index + offset], order[index]];
    change.mutate(() => dashboardWidgetService.reorderWidgets(order));
  };

  const remove = (widgetKey: string) =>
    change.mutate(() => dashboardWidgetService.removeWidget(widgetKey));

  const requestRemoval = (widgetKey: string) => {
    if (subscriptionOf(widgetKey)) {
      setPendingRemoval({ widgetKey, title: titleOf(widgetKey) });
    } else {
      remove(widgetKey);
    }
  };

  const changeSchedule = (widgetKey: string, schedule: WidgetSubscriptionSchedule | null) =>
    change.mutate(() =>
      schedule
        ? dashboardWidgetService.subscribe(widgetKey, schedule)
        : dashboardWidgetService.unsubscribe(widgetKey)
    );

  return (
    <div className={styles.container} data-test-id="my-custom-dashboard">
      <div className={styles.toolbar}>
        <DivisionFilterDropdown
          divisions={divisionsQuery.data ?? []}
          selectedDivisionIds={pendingDivisionIds ?? divisionIds}
          disabled={busy && pendingDivisionIds === null}
          onSelectionChange={changeDivisions}
        />
        <Select
          options={addable.map((widget) => ({ value: widget.key, label: widget.title }))}
          value=""
          placeholder={addable.length > 0 ? ADD_WIDGET_PLACEHOLDER : 'All available widgets are on your Dashboard'}
          disabled={busy || addable.length === 0}
          dataTestId="dashboard-add-widget"
          wrapperClassName={styles.addWidget}
          onChange={(widgetKey) => change.mutate(() => dashboardWidgetService.addWidget(widgetKey))}
        />
      </div>

      {placements.length === 0 ? (
        <p className={styles.empty} data-test-id="my-dashboard-empty">
          Your Dashboard has no widgets yet. Add one to get started.
        </p>
      ) : (
        <div className={styles.widgets} ref={widgetsRef}>
          {placements.map((placement, index) => (
            <DashboardWidgetCard
              key={placement.widget_key}
              widgetKey={placement.widget_key}
              title={titleOf(placement.widget_key)}
              divisionIds={divisionIds}
              subscription={subscriptionOf(placement.widget_key)}
              isFirst={index === 0}
              isLast={index === placements.length - 1}
              busy={busy}
              onMoveUp={() => move(index, -1)}
              onMoveDown={() => move(index, 1)}
              onRemove={() => requestRemoval(placement.widget_key)}
              onScheduleChange={(schedule) => changeSchedule(placement.widget_key, schedule)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingRemoval !== null}
        title="Remove widget"
        message={`Removing ${pendingRemoval?.title ?? 'this widget'} from your Dashboard also ends your email subscription to it.`}
        confirmText="Remove"
        onConfirm={() => {
          if (pendingRemoval) {
            remove(pendingRemoval.widgetKey);
          }
          setPendingRemoval(null);
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </div>
  );
};

export { MyCustomDashboard };
